/**
 * AstorToolRenderers — Registry
 *
 * Maps Astor tool names → typed renderer components. The keys MUST match the
 * fully-qualified tool IDs registered in the agent service. Different tool
 * categories live in different registration sites:
 *   - `8-agent-service/src/tools/backend-tools.service.ts` — case-management,
 *     CRM, and similar domain tools (e.g. `case_management_list_cases`).
 *   - `8-agent-service/src/tools/tool-registry.service.ts` — platform tools
 *     such as `search_knowledge_base`.
 * Use the full registered ID (e.g. `case_management_list_cases`), never an
 * abbreviation like `list_cases`.
 *
 * Issue #1432 Phase 2 — Wave 1 P0, Wave 2 P1, Wave 3 P2
 */

import { CaseDetailCard } from './CaseDetailCard';
import { CasesTable } from './CasesTable';
import { ConsumersTable } from './ConsumersTable';
import { KnowledgeBaseResultList } from './KnowledgeBaseResultList';
import { LeadsTable } from './LeadsTable';
import { PartnerStatsCard } from './PartnerStatsCard';
import { PartnersTable } from './PartnersTable';
import { StatsBreakdown } from './StatsBreakdown';
import type { AstorToolRenderer } from './types';

/**
 * Map tool names (server-side identifiers from 8-agent-service) → typed renderers.
 *
 * The keys MUST match the fully-qualified tool IDs registered in the agent
 * service. Domain tools (case_management_*, crm_*) are registered in
 * `8-agent-service/src/tools/backend-tools.service.ts`; platform tools like
 * `search_knowledge_base` are registered in
 * `8-agent-service/src/tools/tool-registry.service.ts`. Either way, use the
 * full ID (e.g. `case_management_list_cases`) — never an abbreviation.
 *
 * Issue #1432 Phase 2 — Wave 1 P0, Wave 2 P1, Wave 3 P2
 */
export const ASTOR_TOOL_RENDERERS: Record<string, AstorToolRenderer> = {
  // Wave 1 P0. `list_loans` is the loan-language alias of `list_cases` —
  // same data, same renderer (Issue #1432).
  case_management_list_cases: CasesTable,
  case_management_list_loans: CasesTable,
  case_management_get_case: CaseDetailCard,
  // Wave 2 P1
  case_management_list_consumers: ConsumersTable,
  crm_list_partners: PartnersTable,
  case_management_get_stats: StatsBreakdown,
  // Wave 3 P2
  crm_list_leads: LeadsTable,
  crm_get_partner_stats: PartnerStatsCard,
  search_knowledge_base: KnowledgeBaseResultList,
};

/**
 * Lookup a renderer by tool name. Returns null if no renderer is registered —
 * the consuming MessageBubble should fall back to markdown text in that case.
 *
 * Issue #1432 Phase 2
 */
export function getAstorToolRenderer(toolName: string): AstorToolRenderer | null {
  return ASTOR_TOOL_RENDERERS[toolName] ?? null;
}
